import axios from 'axios';
import {
  sanitizePayload,
  sanitizePath,
  sanitizeRequestId,
  sanitizeMessage,
  sanitizeStack,
  sanitizeStatusCode,
  sanitizeCategory,
  boundedString
} from './logSanitiser';

// ERROR_LOGGING_URL is the central host ROOT (https://crm.maine-stream.com);
// this code owns the path suffix. Normalise defensively — an env value with
// a trailing slash or stray /api suffix produces a 404ing double path and
// silently kills telemetry (docs/Error Logging Improvements.md §16).
// Check runtime config first (for Docker), then build-time env var.
const API_URL = ((window as any).REACT_APP_RUNTIME_CONFIG?.ERROR_LOGGING_URL ||
                process.env.REACT_APP_ERROR_LOGGING_URL ||
                'https://crm.maine-stream.com')
  .replace(/\/+$/, '')
  .replace(/\/api$/i, '');
const ERROR_LOG_ENDPOINT = `${API_URL}/api/error-logs`;

// Track if we're currently logging to prevent infinite loops
let isLogging = false;

// Enabled in production by default; other environments opt in explicitly via
// REACT_APP_LOGGING_ENABLED=true. Test runs do NOT auto-enable — unit tests
// exercising emission must set the flag or use forceLog.
const LOGGING_ENABLED =
  process.env.REACT_APP_LOGGING_ENABLED === 'true' ||
  process.env.NODE_ENV === 'production';

// Log levels
export enum LogLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Error categories — must stay within the central ingest enum
// (server/client/network/auth/database/integration).
export enum ErrorCategory {
  SERVER = 'server',
  CLIENT = 'client',
  NETWORK = 'network',
  AUTH = 'auth',
  DATABASE = 'database',
  INTEGRATION = 'integration'
}

interface ErrorLogOptions {
  stack?: string;
  metadata?: Record<string, any>;
  source?: string;
  userId?: string | null;
  statusCode?: number;
  category?: ErrorCategory | string;
  path?: string;
  method?: string;
  requestPayload?: any;
  requestId?: string;
  forceLog?: boolean;
}

interface ErrorLogResponse {
  id: string;
  timestamp: string;
  status: string;
}

// Known third-party errors that are harmless and shouldn't be reported.
// Currently: a known floating-ui/popper positioning bug triggered by
// react-datepicker (used across the app for date fields, e.g. DBS/
// insurance expiry dates) - a middleware function is occasionally
// undefined when the calendar popup positions itself, surfacing as a
// Safari/WebKit-style "X is not a function" unhandled rejection. It does
// not affect the date picker's usability, so it's suppressed here rather
// than fixed upstream in a third-party dependency.
//
// Each entry requires several distinctive tokens from floating-ui's
// internal `MiddlewareState` argument shape to all be present, rather
// than matching on any single generic word, so this can't accidentally
// swallow an unrelated real error that happens to share one term.
const IGNORED_ERROR_TOKEN_SETS: string[][] = [
  ['initialPlacement', 'middlewareData', 'elements']
];

export function isKnownBenignError(message: string): boolean {
  if (!message) return false;
  return IGNORED_ERROR_TOKEN_SETS.some((tokens) => tokens.every((token) => message.includes(token)));
}

// Determine error category based on error details
const determineErrorCategory = (error: any, statusCode?: number): ErrorCategory => {
  // Network errors
  if (
    error?.message?.includes('Network Error') ||
    error?.message?.includes('timeout') ||
    error?.code === 'ECONNABORTED'
  ) {
    return ErrorCategory.NETWORK;
  }

  // Auth errors
  if (statusCode === 401 || statusCode === 403) {
    return ErrorCategory.AUTH;
  }

  // Default to client for frontend errors
  return ErrorCategory.CLIENT;
};

// Main error logging function — everything emitted is sanitised here so no
// call site can leak secrets, PII or credentials into the central store.
// Browser submissions are anonymous by design (no API key in client code):
// the central store records them trusted:false — still worth sanitising so
// nothing sensitive is persisted anywhere.
export const logError = async (
  application: string,
  level: LogLevel,
  message: string,
  options: ErrorLogOptions = {}
): Promise<ErrorLogResponse | null> => {
  // Prevent infinite loops - don't log errors from the error logger itself
  if (isLogging) {
    return null;
  }

  // Suppress known-benign third-party errors (see IGNORED_ERROR_TOKEN_SETS)
  // before they're ever sent, regardless of caller.
  if (isKnownBenignError(message) || isKnownBenignError(options.stack || '')) {
    return null;
  }

  if (!LOGGING_ENABLED && !options.forceLog) {
    return null;
  }

  isLogging = true;

  try {
    const errorCategory = sanitizeCategory(
      options.category || determineErrorCategory(options.metadata?.originalError, options.statusCode)
    );

    const response = await axios.post(ERROR_LOG_ENDPOINT, {
      application,
      level,
      message: sanitizeMessage(message),
      stack: sanitizeStack(options.stack),
      metadata: sanitizePayload(options.metadata),
      source: boundedString(options.source),
      userId: boundedString(options.userId ?? undefined),
      statusCode: sanitizeStatusCode(options.statusCode),
      category: errorCategory,
      path: sanitizePath(options.path ?? window.location.pathname),
      method: boundedString(options.method, 16),
      requestPayload: sanitizePayload(options.requestPayload),
      requestId: sanitizeRequestId(options.requestId),
      environment: boundedString(process.env.NODE_ENV || 'development', 50)
    });

    return response.data;
  } catch (error) {
    // Silently fail - don't log errors about logging errors
    // Only log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to log error to remote server:', error);
    }
    return null;
  } finally {
    isLogging = false;
  }
};

// Create application-specific logger
export const createErrorLogger = (application: string, defaultOptions: ErrorLogOptions = {}) => {
  if (!application) throw new Error('Application name is required');

  return {
    info: (message: string, options: ErrorLogOptions = {}) =>
      logError(application, LogLevel.INFO, message, { ...defaultOptions, ...options }),

    warning: (message: string, options: ErrorLogOptions = {}) =>
      logError(application, LogLevel.WARNING, message, { ...defaultOptions, ...options }),

    error: (messageOrError: string | Error, options: ErrorLogOptions = {}) => {
      if (messageOrError instanceof Error) {
        return logError(application, LogLevel.ERROR, messageOrError.message, {
          stack: messageOrError.stack,
          metadata: { originalError: messageOrError, ...options.metadata },
          ...defaultOptions,
          ...options
        });
      }
      return logError(application, LogLevel.ERROR, messageOrError, { ...defaultOptions, ...options });
    },

    critical: (messageOrError: string | Error, options: ErrorLogOptions = {}) => {
      if (messageOrError instanceof Error) {
        return logError(application, LogLevel.CRITICAL, messageOrError.message, {
          stack: messageOrError.stack,
          metadata: { originalError: messageOrError, ...options.metadata },
          ...defaultOptions,
          ...options
        });
      }
      return logError(application, LogLevel.CRITICAL, messageOrError, { ...defaultOptions, ...options });
    }
  };
};

// Default logger for this application — the claimed identity; the central
// store records the verified identity from the API key (`counselling-crm`)
// for keyed submissions and keeps this value as `claimedApplication`.
export const appLogger = createErrorLogger('counselling-client', { source: 'frontend' });

const errorLoggerExports = { logError, createErrorLogger, appLogger };
export default errorLoggerExports;
